// BootScene (IMPLEMENTATION §2.1, §2.9 "Bake size"): bakes the frame catalog through the REAL atlas path —
// the M8 Canvas2D generators → trimmed frames packed into POT atlas page canvases → textures.addCanvas +
// (trimmed) frames — then starts the FactoryScene.
// Bake policy (M0 decision 2): one bake at bucket·dprEff px per cell, mipmapped, never rebaked on expansion.
import {
  type AtlasOptions,
  type BakedAtlas,
  bakeAtlas,
  type FrameDrawer,
} from '../../art/atlas.ts';
import type { FrameSpec } from '../../art/types.ts';
import { muzzlesOf } from '../juice/geometry.ts';
import { type FrameIndex, registerAtlas } from './frames.ts';
import { type SceneHooks, startScene } from './phaserCompat.ts';

export interface BakeInput extends AtlasOptions {
  frames: readonly FrameSpec[];
  draw: FrameDrawer;
}

export interface BootResult {
  index: FrameIndex;
  /** M10: muzzle socket centroids of the gun frames (cell units of the frame), by frame id. */
  muzzles: ReadonlyMap<string, { x: number; y: number }>;
  pages: number;
  frames: number;
  bakeMs: number;
  pageSizes: { w: number; h: number }[];
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

export function createBootScene(
  bake: () => BakeInput,
  nextKey: string,
  onBaked: (r: BootResult) => void,
): SceneHooks {
  return {
    key: 'boot',
    create(scene) {
      const b = bake();
      const baked: BakedAtlas<HTMLCanvasElement> = bakeAtlas(
        b.frames,
        { cellPx: b.cellPx, pageMax: b.pageMax, padding: b.padding, extrude: b.extrude },
        b.draw,
        makeCanvas,
        () => performance.now(),
      );
      const index = registerAtlas(scene, baked.layout, baked.pages);
      onBaked({
        index,
        muzzles: muzzlesOf(b.frames),
        pages: baked.pages.length,
        frames: index.frames,
        bakeMs: Math.round(baked.ms),
        pageSizes: baked.layout.pages,
      });
      startScene(scene, nextKey);
    },
  };
}
